// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

/**
 * Bridges telemetry's irregular timestamped points onto a plan's `time_axis`.
 *
 * Charts draw by sample index over `raw_inputs.time_axis` (96 × 15 min), but
 * telemetry returns its source cadence with real gaps and accepts no `rollup`.
 * Bucketing is the only correct join — index-by-position would slide the whole
 * curve whenever the cadences differ.
 *
 * Empty buckets stay `null` rather than carrying forward: the chart breaks its
 * line at nulls, so a curve that stops at now reads as "no data yet" instead of
 * a flat prediction to midnight.
 */

import { axisStartMs } from './planSeries';
import type { TimeAxis } from '../types';

export interface ResamplePoint {
  /** ISO-8601 timestamp. */
  timestamp: string;
  value: number | null;
}

/**
 * Drop each point into its axis bucket, discarding what neither resampler can
 * use. Points outside `[axisStart, axisStart + count * step)` are dropped, not
 * clamped: a reading from yesterday is not evidence about this axis's first
 * bucket. Non-finite and null values go the same way.
 */
function bucketize(
  points: ResamplePoint[] | null | undefined,
  timeAxis: TimeAxis
): { count: number; stepMs: number; start: number; usable: Bucketed[] } | null {
  const count = timeAxis?.count ?? 0;
  const stepMs = (timeAxis?.step_seconds ?? 0) * 1000;
  const start = axisStartMs(timeAxis);
  if (count <= 0 || stepMs <= 0 || start === undefined) return null;

  const usable: Bucketed[] = [];
  for (const point of points ?? []) {
    const value = point?.value;
    if (value === null || value === undefined || !Number.isFinite(value)) continue;
    const at = new Date(point.timestamp).getTime();
    if (Number.isNaN(at)) continue;
    const bucket = Math.floor((at - start) / stepMs);
    if (bucket < 0 || bucket >= count) continue;
    usable.push({ at, bucket, value });
  }
  return { count, stepMs, start, usable };
}

interface Bucketed {
  at: number;
  bucket: number;
  value: number;
}

/** Average each axis bucket's points, `null` where a bucket has none. */
export function resampleToAxis(
  points: ResamplePoint[] | null | undefined,
  timeAxis: TimeAxis
): (number | null)[] {
  const bucketed = bucketize(points, timeAxis);
  if (!bucketed) return [];

  const sums = new Array<number>(bucketed.count).fill(0);
  const hits = new Array<number>(bucketed.count).fill(0);
  for (const { bucket, value } of bucketed.usable) {
    sums[bucket] += value;
    hits[bucket] += 1;
  }

  return sums.map((sum, i) => (hits[i] === 0 ? null : sum / hits[i]));
}

/**
 * Linearly fill the null buckets that lie **between** two real readings, leaving
 * the leading and trailing nulls untouched.
 *
 * A source coarser than the axis — hourly weather on a 15-minute axis — lands
 * each reading in one bucket with empty buckets either side, so the curve has no
 * two adjacent points to connect and {@link resampleToAxis}'s honest nulls draw
 * as nothing. Interpolating the interior gaps joins those isolated points into a
 * continuous line, while the untouched trailing nulls keep the curve stopping
 * where measurement stops rather than running a flat line to the axis end.
 *
 * Deliberately interior-only: a leading null is a reading not yet taken and a
 * trailing null is the "stops at now" edge — neither is a gap between two
 * knowns, so there is nothing to interpolate across. Only for continuous
 * quantities where a mid-point is a plausible value (temperature); a discrete
 * series would be misrepresented and should not use this.
 */
export function fillInteriorGaps(values: (number | null)[]): (number | null)[] {
  const out = values.slice();
  let prev = -1;
  for (let i = 0; i < out.length; i += 1) {
    const value = out[i];
    if (value === null || value === undefined || !Number.isFinite(value)) continue;
    if (prev >= 0 && i - prev > 1) {
      const from = out[prev] as number;
      const span = i - prev;
      for (let k = prev + 1; k < i; k += 1) {
        out[k] = from + ((value - from) * (k - prev)) / span;
      }
    }
    prev = i;
  }
  return out;
}

/**
 * Bucket each axis slot to the value that held for the longest time in it.
 *
 * The counterpart to {@link resampleToAxis}, for **discrete** quantities where a
 * mean is not a member of the value set: a chiller count steps from 7 to 8 and
 * is never 7.4, so a bucket that saw both stages is whichever ran longer.
 *
 * Each point is weighted by the gap to the next, because change-of-value
 * publishing makes a reading a claim that holds until the next one. Weights clip
 * at the bucket edge, and ties go to the later value — a tie means the quantity
 * genuinely changed mid-bucket, so the later state is the one still true there.
 */
export function resampleToAxisDominant(
  points: ResamplePoint[] | null | undefined,
  timeAxis: TimeAxis
): (number | null)[] {
  const bucketed = bucketize(points, timeAxis);
  if (!bucketed) return [];
  const { count, stepMs, start, usable } = bucketed;

  // Dwell is a function of order, so this cannot lean on the caller's ordering
  // the way an average can.
  usable.sort((a, b) => a.at - b.at);

  // value -> [total dwell ms, position of its last occurrence], per bucket.
  const held = new Array<Map<number, [number, number]>>(count);

  usable.forEach((point, index) => {
    const next = usable[index + 1];
    // The final point has no successor to bound it, so it inherits the previous
    // gap; a lone point holds for its whole bucket.
    const span = next
      ? next.at - point.at
      : usable[index - 1]
        ? point.at - usable[index - 1].at
        : stepMs;
    const bucketEnd = start + (point.bucket + 1) * stepMs;
    const dwell = Math.max(Math.min(span, bucketEnd - point.at), 0);

    const bucket = held[point.bucket] ?? new Map<number, [number, number]>();
    const [total] = bucket.get(point.value) ?? [0, 0];
    bucket.set(point.value, [total + dwell, index]);
    held[point.bucket] = bucket;
  });

  return Array.from({ length: count }, (_, i) => {
    const bucket = held[i];
    if (!bucket) return null;
    let best: number | null = null;
    let bestDwell = -1;
    let bestPosition = -1;
    bucket.forEach(([dwell, position], value) => {
      if (dwell > bestDwell || (dwell === bestDwell && position > bestPosition)) {
        best = value;
        bestDwell = dwell;
        bestPosition = position;
      }
    });
    return best;
  });
}

/**
 * Element-wise sum of several axis-aligned series. A bucket is `null` only when
 * **every** member is null there: summing a partially-reported bucket as if the
 * silent members were zero would draw a dip that looks like the plan shedding
 * load, so non-reporting members are excluded from that timestep instead.
 */
export function sumSeries(seriesList: (number | null)[][]): (number | null)[] {
  const length = Math.max(0, ...seriesList.map(s => s.length));
  const out: (number | null)[] = [];
  for (let i = 0; i < length; i += 1) {
    let total = 0;
    let contributors = 0;
    for (const series of seriesList) {
      const value = series[i];
      if (value === null || value === undefined || !Number.isFinite(value)) continue;
      total += value;
      contributors += 1;
    }
    out.push(contributors === 0 ? null : total);
  }
  return out;
}
